import { useEffect, useState } from 'react';
import { ArrowLeftRight } from 'lucide-react';
import apiClient from '../api/client';
import { transferAgent } from '../api/agentTransfer';
import { useToast } from './ui/toastContext';

interface TenantOption {
  id: string;
  name: string;
}

interface MeResponse {
  platform_role?: string;
  tenants?: { tenant_id: string; slug?: string; name: string; role?: string }[];
}

interface AgentTransferDialogProps {
  agentId: string;
  agentName: string;
  /** 转移成功后回调（如刷新列表） */
  onTransferred?: () => void;
  onClose: () => void;
}

const getApiErrorMessage = (e: unknown): string => {
  const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (typeof err.message === 'string' && err.message) return err.message;
  return '未知错误';
};

/**
 * Agent 归属转移对话框：输入目标邮箱 + 选择目标租户后提交转移。
 * 租户数据源与顶栏 TenantSwitcher 一致：/auth/me 判角色——
 * 超管拉 GET /tenants 全量，非超管用当前用户所属租户列表。
 */
export default function AgentTransferDialog({
  agentId,
  agentName,
  onTransferred,
  onClose,
}: AgentTransferDialogProps) {
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [tenantId, setTenantId] = useState('');
  // 弹窗挂载即加载租户列表，故初始即为加载态
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<MeResponse>('/auth/me')
      .then((meRes) => {
        const isSuperAdmin = (meRes.data?.platform_role || '').toLowerCase() === 'super_admin';
        if (isSuperAdmin) {
          return apiClient.get<TenantOption[]>('/tenants').then((tenantsRes) => ({
            options: (tenantsRes.data ?? []).map((t) => ({ id: t.id, name: t.name })),
          }));
        }
        return {
          options: (meRes.data?.tenants ?? []).map((t) => ({ id: t.tenant_id, name: t.name })),
        };
      })
      .then(({ options }) => {
        if (cancelled) return;
        setTenants(options);
        setTenantId(options[0]?.id ?? '');
      })
      .catch(() => {
        // 角色/租户加载失败不阻塞表单，提交时后端仍会校验成员关系
        if (!cancelled) setTenants([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setEmailError('请输入目标账号邮箱');
      return;
    }
    if (!tenantId) {
      toast.error('请选择目标租户');
      return;
    }
    setEmailError('');
    setSubmitting(true);
    try {
      const result = await transferAgent(agentId, {
        target_email: trimmed,
        target_tenant_id: tenantId,
      });
      toast.success(result.warnings.length ? `${result.detail}；${result.warnings.join('；')}` : result.detail);
      onTransferred?.();
      onClose();
    } catch (e) {
      toast.error('转移失败: ' + getApiErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="转移归属"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-secondary)',
          border: '1px solid var(--bg-tertiary)',
          borderRadius: 'var(--radius-lg)',
          padding: 20,
          width: 'min(420px, 100%)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <ArrowLeftRight size={18} color="var(--color-primary)" />
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>
            转移归属：{agentName}
          </h3>
        </div>
        <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-secondary)' }}>
          转移后该 Agent 及其配置档案将归属目标用户与目标租户，操作会记录审计。
        </p>
        <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
          目标账号邮箱
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError('');
          }}
          placeholder="对方需已登录过本平台"
          aria-label="目标账号邮箱"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '8px 10px',
            fontSize: 13,
            color: 'var(--text-primary)',
            background: 'var(--bg-primary)',
            border: `1px solid ${emailError ? 'var(--color-error)' : 'var(--bg-tertiary)'}`,
            borderRadius: 'var(--radius-lg)',
            outline: 'none',
            marginBottom: emailError ? 4 : 12,
          }}
        />
        {emailError && (
          <div style={{ fontSize: 12, color: 'var(--color-error)', marginBottom: 8 }}>{emailError}</div>
        )}
        <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
          目标租户
        </label>
        <select
          className="input"
          aria-label="目标租户"
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
          disabled={loading || tenants.length === 0}
          style={{ width: '100%', marginBottom: 16, padding: '8px 10px', fontSize: 13 }}
        >
          {loading && <option value="">加载中…</option>}
          {!loading && tenants.length === 0 && <option value="">暂无可选租户</option>}
          {!loading &&
            tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
        </select>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '7px 16px',
              fontSize: 13,
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              background: 'var(--bg-tertiary)',
              border: 'none',
              borderRadius: 'var(--radius-lg)',
            }}
          >
            取消
          </button>
          <button
            type="button"
            disabled={submitting || loading}
            onClick={() => void handleSubmit()}
            style={{
              padding: '7px 16px',
              fontSize: 13,
              cursor: submitting || loading ? 'not-allowed' : 'pointer',
              opacity: submitting || loading ? 0.5 : 1,
              color: 'var(--text-on-success)',
              background: 'var(--color-primary)',
              border: 'none',
              borderRadius: 'var(--radius-lg)',
              fontWeight: 600,
            }}
          >
            {submitting ? '转移中…' : '确认转移'}
          </button>
        </div>
      </div>
    </div>
  );
}
